import { FocusMonitor } from '@angular/cdk/a11y'
import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { Component, ElementRef, Input, OnDestroy, OnInit, Optional, Self } from '@angular/core'
import { FormControl, FormGroupDirective, NgControl, NgForm } from '@angular/forms'
import { MatFormFieldControl } from '@angular/material/form-field'
import { ClinicService, Facility } from '@mmx/shared'
import { filter, first, orderBy } from 'lodash'
import { ReplaySubject, Subject } from 'rxjs'
import { takeUntil, tap } from 'rxjs/operators'

import { CustomMaterialInputControlComponent } from '../custom-field'

type Value = string | string[] | Facility | Facility[]

@Component({
  selector: 'mmx-facility-selector-input',
  templateUrl: './facility-selector.component.html',
  styleUrls: ['./facility-selector.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: FacilitySelectorFieldComponent,
    },
  ],
  standalone: false,
})
export class FacilitySelectorFieldComponent
  extends CustomMaterialInputControlComponent<Value>
  implements OnInit, OnDestroy
{
  locationFormControl = new FormControl<Value>(null)
  locations: Facility[] = []
  facilityIds: string[] = []
  filteredLocations: ReplaySubject<Facility[]> = new ReplaySubject<Facility[]>(
    1,
  )
  locationFilterCtrl = new FormControl<string>(null)
  @Input() facilityId: string | string[]
  @Input() fullResponse = false
  @Input() multiple = false
  @Input() selectAll = true

  @Input()
  get value(): Value | null {
    return this._value
  }
  set value(locations: Value | null) {
    this._value = locations
    this.updateControlValue()
    this.stateChanges.next()
  }

  get isMultiple(): boolean {
    return coerceBooleanProperty(this.multiple)
  }

  get isFull(): boolean {
    return coerceBooleanProperty(this.fullResponse)
  }

  protected _placeholder = 'common.facility'
  protected _value: Value

  private onDestroy$ = new Subject<void>()

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() protected parentForm: NgForm,
    @Optional() protected parentFormGroup: FormGroupDirective,
    protected fm: FocusMonitor,
    protected elRef: ElementRef,
    private clinicService: ClinicService,
  ) {
    super(ngControl, parentForm, parentFormGroup, fm, elRef)
  }

  ngOnInit() {
    super.ngOnInit()

    this.clinicService.getLocations().pipe(takeUntil(this.onDestroy$)).subscribe((locations) => {
      this.locations = filter(orderBy(locations, 'name'), 'enabled')
      this.facilityIds = this.locations.map((f) => f.id)

      // if the value was a locationId, transform it into a location now that we loaded the locations
      if (this._value != null || this.facilityId) {
        this.writeValue(this._value || this.facilityId)
      }

      this.filteredLocations.next(this.locations.slice())

      this.locationFilterCtrl.valueChanges.pipe(
        takeUntil(this.onDestroy$),
        tap((search) => this.filterLocation(search)),
      ).subscribe()

      this.locationFormControl.valueChanges.subscribe((value) => {
        this._value = value
        this.stateChanges.next()
        this.onChange(this.value)
      })

      if (this.locations.length === 1 && !this.isMultiple) {
        this.locationFormControl.setValue(first(this.locations)?.id)
        this.locationFormControl.updateValueAndValidity()
      } else if (this.value != null) {
        this.updateControlValue()
      }
    })
  }

  ngOnDestroy(): void {
    super.ngOnDestroy()
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  writeValue(value: Value) {
    if (typeof value === 'string') {
      this.value = this.isFull ? this.locations.filter(location => location.id === value) : [value]
    } else if (value instanceof Facility) {
      this.value = this.isFull ? [value] : [value.id]
    } else if (Array.isArray(value)) {
      if (value[0] instanceof Facility && !this.isFull) { // Array of facilities
        this.value = value.map(facility => facility.id)
      } else if (typeof value[0] === 'string' && this.isFull) { // Array of facilityIds
        this.value = value.map(id => this.locations.find(location => location.id === id))
      } else {
        this.value = value
      }
    }
  }

  private updateControlValue() {
    if (this.isMultiple) {
      this.locationFormControl.setValue(this._value)
    } else if (this._value != null) {
      this.locationFormControl.setValue(this._value[0])
    } else {
      this.locationFormControl.setValue(null)
    }

    this.locationFormControl.updateValueAndValidity()
  }

  private filterLocation(search?: string) {
    if (!this.locations) {
      return
    } else if (!search) {
      this.filteredLocations.next(this.locations.slice())
      return
    }

    search = search.toLowerCase()

    // filter the facilities
    this.filteredLocations.next(
      this.locations.filter(
        (location) => location.name.toLowerCase().indexOf(search) > -1,
      ),
    )
  }
}
